"use client";

// Live server pill for the site header. Subscribes to the shared SSE stream
// (/api/sse/group) and reads the server-scoped connection state + AppInfo
// (players / maxPlayers / mapSize / wipeTime) for the user's bound serverKey.
// Reuses the same event shapes the map consumes (server.connection_state +
// server.info). Hovering (or focusing) the pill reveals a tooltip with last
// wipe, player count, and map size. Degrades to just the server name when the
// stream isn't connected yet.

import { useCallback, useEffect, useState } from "react";
import { useSSE } from "@/hooks/useSSE";
import type { RealtimeEvent } from "@/lib/realtime/events";

type ConnState = "connected" | "connecting" | "disconnected" | "reconnecting" | "unknown";

// Below this gap we treat the stream as keeping up and report "None"; past
// it (e.g. workers rolling) we show how long since the last payload.
const FRESH_MS = 8_000;

function formatDataDelay(lastDataAt: number | null): string {
  if (lastDataAt == null) return "—";
  const delta = Date.now() - lastDataAt;
  if (delta < FRESH_MS) return "None";
  const secs = Math.floor(delta / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

export function HeaderServerInfo({
  serverKey,
  serverName,
  userTeamId,
}: {
  serverKey: string;
  serverName: string;
  // The user's own pairing.team_id, or null if discovery hasn't
  // stamped it yet. Used to filter server.connection_state events so
  // OTHER teams' state on the same server can't paint this user's pill.
  // Null leaves the pill in its "unknown" state (falls through to SSE
  // state), which is correct: we don't know our own team's worker
  // health yet.
  userTeamId: string | null;
}) {
  const [conn, setConn] = useState<ConnState>("unknown");
  const [players, setPlayers] = useState<number | null>(null);
  const [maxPlayers, setMaxPlayers] = useState<number | null>(null);
  const [mapSize, setMapSize] = useState<number | null>(null);
  const [wipeTime, setWipeTime] = useState<number | null>(null);
  const [lastDataAt, setLastDataAt] = useState<number | null>(null);
  const [hovered, setHovered] = useState(false);

  // While the tooltip is open, tick once a second so the "Data Delay"
  // counter keeps climbing even when no events are arriving.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hovered) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [hovered]);

  const onEvent = useCallback(
    (event: RealtimeEvent) => {
      // Any server-scoped payload for our server counts as fresh data.
      // (sse.ping carries no payload — and never reaches consumers anyway.)
      const sk = "payload" in event
        ? (event.payload as { serverKey?: string }).serverKey
        : undefined;
      if (sk === serverKey) setLastDataAt(Date.now());
      if (event.type === "server.connection_state"
          && event.payload.serverKey === serverKey
          && userTeamId != null
          && event.payload.teamId === userTeamId) {
        setConn(event.payload.state as ConnState);
      } else if (event.type === "server.info" && event.payload.serverKey === serverKey) {
        const raw = event.payload.info as { info?: Record<string, unknown> } & Record<string, unknown>;
        const info = (raw?.info ?? raw) as Record<string, unknown>;
        if (typeof info?.players === "number") setPlayers(info.players);
        if (typeof info?.maxPlayers === "number") setMaxPlayers(info.maxPlayers);
        if (typeof info?.mapSize === "number") setMapSize(info.mapSize);
        if (typeof info?.wipeTime === "number") setWipeTime(info.wipeTime);
      }
    },
    [serverKey, userTeamId],
  );

  const { state: sseState } = useSSE("/api/sse/group", onEvent);

  // The worker ↔ Rust+ connection state takes priority once we've seen it;
  // before that, fall back to the browser ↔ control-plane SSE state.
  const dotColor =
    conn === "connected" ? "#22c55e"
    : conn === "connecting" || conn === "reconnecting" ? "#eab308"
    : conn === "disconnected" ? "#ef4444"
    : sseState === "connected" ? "#eab308"
    : "#6b7280";

  const wipeLabel = wipeTime != null
    ? new Date(wipeTime * 1000).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "—";

  return (
    <span style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
      <button
        type="button"
        // Button instead of span so a tap on mobile focuses it — which
        // already opens the tooltip via onFocus. The pill is otherwise
        // visually identical and remains a "read-only" affordance:
        // there's no onClick action beyond toggling focus.
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{
          display: "inline-flex", alignItems: "center", gap: "0.5rem", maxWidth: "100%",
          padding: "0.3rem 0.7rem", borderRadius: 999, border: "1px solid var(--border)",
          background: "var(--bg)", fontSize: "0.85rem", color: "var(--fg)",
          cursor: "default", outline: "none", font: "inherit",
        }}
      >
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }}
        />
        <span
          className="header-server-name"
          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {serverName}
        </span>
        {players != null ? (
          <span style={{ color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
            {players}/{maxPlayers ?? "?"}
          </span>
        ) : null}
      </button>

      {hovered ? (
        <span
          role="tooltip"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 100,
            minWidth: 180, padding: "0.5rem 0.7rem",
            background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: 8,
            boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
            fontSize: "0.8rem", color: "var(--fg)",
            display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.25rem 0.75rem",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "var(--fg-muted)" }}>Last Wipe</span>
          <span style={{ textAlign: "right" }}>{wipeLabel}</span>
          <span style={{ color: "var(--fg-muted)" }}>Players</span>
          <span style={{ textAlign: "right" }}>{players != null ? `${players}/${maxPlayers ?? "?"}` : "—"}</span>
          <span style={{ color: "var(--fg-muted)" }}>Map Size</span>
          <span style={{ textAlign: "right" }}>{mapSize != null ? mapSize : "—"}</span>
          <span style={{ color: "var(--fg-muted)" }}>Data Delay</span>
          <span style={{ textAlign: "right" }}>{formatDataDelay(lastDataAt)}</span>
        </span>
      ) : null}
    </span>
  );
}
